import { BILLING_ADDRESS_MODE_FULL, PAGE_TEXT } from "./paymentPage.constants";
import { formatMinorUnitsAsMoney } from "./currencyMinorUnits";

export const NO_ORDER_SUMMARY = Object.freeze({ isAvailable: false });

// A link whose order the page cannot show (its amount is not a whole number of an Adyen currency's
// minor units): the page says so beside whatever became of the payment.
export const ORDER_SUMMARY_UNREADABLE = Object.freeze({ isAvailable: false, isUnreadable: true });

export function resolveClientKey(environment) {
    if (!["test", "live"].includes(environment)) throw new Error("Unsupported payment environment.");
    const key = environment === "test" ? process.env.REACT_APP_ADYEN_CLIENT_KEY_TEST : process.env.REACT_APP_ADYEN_CLIENT_KEY;
    if (typeof key !== "string" || !key.startsWith(environment + "_")) {
        throw new Error(`The ${environment} payment client key is missing or invalid.`);
    }
    return key;
}

// A card save shows no amount. The description is the link's document reference ("Reference" and
// the order) or, without one, the vendor, as the payment page API gives it; the consent wording a
// card is saved under is shown and recorded where the shopper agreed to it, not here.
export function buildOrderSummary(paymentLinkPage) {
    if (paymentLinkPage.isCardSaving) {
        return Object.freeze({
            isAvailable: true,
            isCardSaving: true,
            vendor: paymentLinkPage.vendor,
            description: paymentLinkPage.description,
            reference: PAGE_TEXT.REFERENCE_PREFIX + paymentLinkPage.reference,
        });
    }
    return Object.freeze({
        isAvailable: true,
        isCardSaving: false,
        // A subscription order: the session stores the card without asking, and the page says so.
        isStorePaymentMethodRequired: paymentLinkPage.isStorePaymentMethodRequired === true,
        vendor: paymentLinkPage.vendor,
        description: paymentLinkPage.description,
        amount: formatMinorUnitsAsMoney(paymentLinkPage.amount.value, paymentLinkPage.amount.currency),
        reference: PAGE_TEXT.REFERENCE_PREFIX + paymentLinkPage.reference,
    });
}

export function buildCheckoutConfiguration(paymentLinkPage, checkoutSession, dropinCallbacks) {
    return {
        environment: paymentLinkPage.environment,
        clientKey: resolveClientKey(paymentLinkPage.environment),
        session: { id: checkoutSession.sessionId, sessionData: checkoutSession.sessionData },
        ...dropinCallbacks,
    };
}

function applyCardSavingConfiguration(dropinConfiguration, paymentLinkPage) {
    if (paymentLinkPage.isCardSaving) {
        return { ...dropinConfiguration, showStoredPaymentMethods: false };
    }
    return dropinConfiguration;
}

function applyBillingAddressConfiguration(dropinConfiguration, paymentLinkPage) {
    if (paymentLinkPage.isBillingAddressRequired === false) {
        return dropinConfiguration;
    }
    return {
        ...dropinConfiguration,
        paymentMethodsConfiguration: {
            card: {
                billingAddressRequired: true,
                billingAddressMode: BILLING_ADDRESS_MODE_FULL,
                data: { billingAddress: paymentLinkPage.billingAddress },
            },
        },
    };
}

export function buildDropinConfiguration(paymentLinkPage) {
    const baseConfiguration = { showPayButton: true };
    const savingConfiguration = applyCardSavingConfiguration(baseConfiguration, paymentLinkPage);
    return applyBillingAddressConfiguration(savingConfiguration, paymentLinkPage);
}

// The order summary after the shopper has paid and returned: an order the page cannot show is said
// to be unreadable, and never stops the payment's result being shown.
export function readOrderSummaryAfterPayment(paymentLinkPage) {
    try {
        return buildOrderSummary(paymentLinkPage);
    } catch (error) {
        console.error(error);
        return ORDER_SUMMARY_UNREADABLE;
    }
}
